import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Info, Loader2, Save, Plus, Bell, History } from 'lucide-react';
import { extractApiErrorMessage, networkErrorMessage } from '../lib/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Increments a version string in x.x.x format with auto-carry at 9.
 * Example: "1.1.9" → "1.2.0", "1.9.9" → "2.0.0"
 */
function bumpVersion(v) {
  const parts = (v || '0.0.0').split('.').map((n) => parseInt(n, 10) || 0);
  while (parts.length < 3) parts.push(0);
  let [maj, min, pat] = parts;
  pat += 1;
  if (pat > 9) { pat = 0; min += 1; }
  if (min > 9) { min = 0; maj += 1; }
  return `${maj}.${min}.${pat}`;
}

const VERSION_REGEX = /^\d{1,3}\.\d{1,3}\.\d{1,3}$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

export default function InfoSystemPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);

  // Version state
  const [version, setVersion] = useState('');
  const [savedVersion, setSavedVersion] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  // Changelog (somente leitura — entradas são redigidas pelo dev, sem formulário)
  const [changelog, setChangelog] = useState([]);

  // Notifications state
  const [notifAccount, setNotifAccount] = useState(false);
  const [notifSubs, setNotifSubs] = useState(false);
  const [notifLifecycle, setNotifLifecycle] = useState(false);
  const [notifEmail, setNotifEmail] = useState('');
  const [savedNotifAccount, setSavedNotifAccount] = useState(false);
  const [savedNotifSubs, setSavedNotifSubs] = useState(false);
  const [savedNotifLifecycle, setSavedNotifLifecycle] = useState(false);
  const [savedNotifEmail, setSavedNotifEmail] = useState('');

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/super/system-info`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setVersion(data.version || '');
          setSavedVersion(data.version || '');
          setUpdatedAt(data.updated_at);
          setChangelog(data.changelog || []);
          setNotifAccount(!!data.notify_account_events);
          setSavedNotifAccount(!!data.notify_account_events);
          setNotifSubs(!!data.notify_subscription_events);
          setSavedNotifSubs(!!data.notify_subscription_events);
          setNotifLifecycle(!!data.notify_lifecycle_events);
          setSavedNotifLifecycle(!!data.notify_lifecycle_events);
          setNotifEmail(data.notifications_email || '');
          setSavedNotifEmail(data.notifications_email || '');
        } else {
          toast.error(await extractApiErrorMessage(res, 'Erro ao carregar informações'));
        }
      } catch (e) {
        toast.error(networkErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isVersionValid = VERSION_REGEX.test(version);
  const hasVersionChanges = version !== savedVersion;

  const anyToggleOn = notifAccount || notifSubs || notifLifecycle;
  const isNotifEmailValid = !notifEmail || EMAIL_REGEX.test(notifEmail);
  const hasNotifChanges =
    notifAccount !== savedNotifAccount ||
    notifSubs !== savedNotifSubs ||
    notifLifecycle !== savedNotifLifecycle ||
    notifEmail.trim().toLowerCase() !== (savedNotifEmail || '').trim().toLowerCase();
  const canSaveNotif =
    isNotifEmailValid && (!anyToggleOn || (!!notifEmail && EMAIL_REGEX.test(notifEmail)));

  const handleIncrement = () => {
    if (!VERSION_REGEX.test(version)) {
      setVersion('0.0.1');
      return;
    }
    setVersion(bumpVersion(version));
  };

  const handleSaveVersion = async () => {
    if (!isVersionValid) {
      toast.error('Versão inválida. Use o formato x.x.x (ex: 1.2.0).');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/super/system-info`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ version }),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedVersion(data.version);
        setUpdatedAt(new Date().toISOString());
        toast.success(`Versão atualizada para ${data.version}`);
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao salvar'));
      }
    } catch (e) {
      toast.error(networkErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  // Changelog agrupado por data (o backend já entrega mais recentes primeiro).
  const changelogByDate = [];
  for (const entry of changelog) {
    const last = changelogByDate[changelogByDate.length - 1];
    if (last && last.date === entry.date) last.items.push(entry);
    else changelogByDate.push({ date: entry.date, items: [entry] });
  }

  const handleSaveNotif = async () => {
    if (anyToggleOn && !EMAIL_REGEX.test(notifEmail)) {
      toast.error('Informe um email válido para receber as notificações.');
      return;
    }
    setSavingNotif(true);
    try {
      const res = await fetch(`${API_URL}/api/super/system-info`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          notify_account_events: notifAccount,
          notify_subscription_events: notifSubs,
          notify_lifecycle_events: notifLifecycle,
          notifications_email: notifEmail.trim().toLowerCase(),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedNotifAccount(!!data.notify_account_events);
        setSavedNotifSubs(!!data.notify_subscription_events);
        setSavedNotifLifecycle(!!data.notify_lifecycle_events);
        setSavedNotifEmail(data.notifications_email || '');
        setNotifEmail(data.notifications_email || '');
        setUpdatedAt(new Date().toISOString());
        const enabled = [];
        if (data.notify_account_events) enabled.push('Criação/Exclusão');
        if (data.notify_subscription_events) enabled.push('Assinatura');
        if (data.notify_lifecycle_events) enabled.push('Ciclo de vida');
        toast.success(
          enabled.length
            ? `Notificações ativas (${enabled.join(' + ')}) → ${data.notifications_email}`
            : 'Notificações desativadas.'
        );
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao salvar notificações'));
      }
    } catch (e) {
      toast.error(networkErrorMessage(e));
    } finally {
      setSavingNotif(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="info-system-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Info Sistema</h1>
        <p className="text-muted-foreground">
          Configurações gerais do sistema.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Card 1 — Version */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Versão do Sistema
          </CardTitle>
          <CardDescription>
            Versão exibida na tela de login. Use o formato <strong>x.x.x</strong>.
            <br />
            Ao incrementar, segue regra de carry: <code>1.1.9 → 1.2.0</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="version">Versão atual</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="version"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="1.0.0"
                    className={`max-w-[160px] font-mono text-base ${!isVersionValid && version ? 'border-red-500' : ''}`}
                    data-testid="version-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleIncrement}
                    disabled={saving}
                    data-testid="version-increment-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Incrementar
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSaveVersion}
                    disabled={saving || !isVersionValid || !hasVersionChanges}
                    data-testid="version-save-btn"
                  >
                    {saving
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Save className="w-4 h-4 mr-2" />}
                    Salvar
                  </Button>
                </div>
                {!isVersionValid && version && (
                  <p className="text-xs text-red-600" data-testid="version-error">
                    Formato inválido. Use x.x.x (ex: 1.2.0).
                  </p>
                )}
                {updatedAt && (
                  <p className="text-xs text-muted-foreground">
                    Última atualização: {new Date(updatedAt).toLocaleString('pt-BR')}
                  </p>
                )}
              </div>

              {/* Changelog — janela rolável, data em negrito, descrições abaixo (somente leitura) */}
              {changelogByDate.length > 0 && (
                <div className="pt-4 border-t space-y-2">
                  <p className="flex items-center gap-2 font-semibold leading-none tracking-tight">
                    <History className="w-5 h-5" />
                    Changelog
                  </p>
                  <div
                    className="border rounded-md p-3 space-y-3 h-[21rem] overflow-y-auto bg-secondary/20"
                    data-testid="changelog-list"
                  >
                    {changelogByDate.map((group) => (
                      <div key={group.date}>
                        <p className="text-sm font-bold">
                          {group.date ? new Date(group.date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                        </p>
                        <ul className="mt-1 space-y-1">
                          {group.items.map((entry) => (
                            <li key={entry.id} className="text-sm text-muted-foreground flex gap-2">
                              <span className="text-muted-foreground/60">•</span>
                              <span className="whitespace-pre-wrap">{entry.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Card 2 — Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notificações do Sistema
          </CardTitle>
          <CardDescription>
            Receba emails sempre que ocorrer um evento importante na plataforma.
            Você pode habilitar cada categoria de forma independente.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Toggle 1: Account events */}
              <div className="flex items-center justify-between p-4 border bg-secondary/30 rounded-lg">
                <div className="space-y-0.5 pr-3">
                  <Label htmlFor="notif-account-toggle" className="font-medium">
                    Criação / Exclusão de conta
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Email enviado quando um novo signup ocorre (Trial ou pago) e quando um usuário exclui a própria conta (LGPD).
                  </p>
                </div>
                <Switch
                  id="notif-account-toggle"
                  checked={notifAccount}
                  onCheckedChange={setNotifAccount}
                  data-testid="notifications-account-switch"
                />
              </div>

              {/* Toggle 2: Subscription events */}
              <div className="flex items-center justify-between p-4 border bg-secondary/30 rounded-lg">
                <div className="space-y-0.5 pr-3">
                  <Label htmlFor="notif-subs-toggle" className="font-medium">
                    Alteração de assinatura
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Email enviado quando um cliente troca de plano, altera o status ou cancela a assinatura no Stripe.
                  </p>
                </div>
                <Switch
                  id="notif-subs-toggle"
                  checked={notifSubs}
                  onCheckedChange={setNotifSubs}
                  data-testid="notifications-subscription-switch"
                />
              </div>

              {/* Toggle 3: Lifecycle events (trial-end + past-due block) */}
              <div className="flex items-center justify-between p-4 border bg-secondary/30 rounded-lg">
                <div className="space-y-0.5 pr-3">
                  <Label htmlFor="notif-lifecycle-toggle" className="font-medium">
                    Eventos de ciclo de vida
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Email enviado quando o período de teste de uma conta termina (por tempo ou limite) e quando uma conta paga é bloqueada por atraso de pagamento (a partir do 3º dia).
                  </p>
                </div>
                <Switch
                  id="notif-lifecycle-toggle"
                  checked={notifLifecycle}
                  onCheckedChange={setNotifLifecycle}
                  data-testid="notifications-lifecycle-switch"
                />
              </div>

              {/* Shared email */}
              <div className="space-y-2 pt-2">
                <Label htmlFor="notif-email">Email de notificações</Label>
                <Input
                  id="notif-email"
                  type="email"
                  value={notifEmail}
                  onChange={(e) => setNotifEmail(e.target.value)}
                  placeholder="superadmin@watizap.com"
                  className={!isNotifEmailValid && notifEmail ? 'border-red-500' : ''}
                  disabled={savingNotif}
                  data-testid="notifications-email-input"
                />
                {!isNotifEmailValid && notifEmail && (
                  <p className="text-xs text-red-600" data-testid="notifications-email-error">
                    Email inválido. Use o formato nome@dominio.com.
                  </p>
                )}
                {anyToggleOn && !notifEmail && (
                  <p className="text-xs text-amber-600">
                    Para ativar as notificações, informe um email de destino.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Este email recebe as notificações de ambos os toggles acima.
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  onClick={handleSaveNotif}
                  disabled={savingNotif || !hasNotifChanges || !canSaveNotif}
                  data-testid="notifications-save-btn"
                >
                  {savingNotif
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Save className="w-4 h-4 mr-2" />}
                  Salvar notificações
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
